/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { StyledText } from "../../../assets/typography/typography";
import { css } from "@emotion/react";
import { colors } from "../../../components/colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { menuIconStyle } from "../../common/DropdownItemLink";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ConfirmActionModal } from "../../../components/ConfirmActionModal";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../appRoutingHelpers";
import { toastr } from "react-redux-toastr";
import { DeleteButton } from "../../../components/NewButtons";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { useDeleteMenu } from "../service/menus-api";
import { getErrorMessage } from "../../../error-handler";

export type ButtonType = "dropdown" | "regular";

type Props = {
  onClick?: () => void;
  menuId: string;
};

const deleteButtonStyle = css`
  display: flex;
  align-items: center;
  margin-left: 8px;
  cursor: pointer;
  border-radius: 4px;
  margin-right: 8px;
  margin-bottom: 8px;
  padding: 5px 12px 5px 12px;
  gap: 8px;
  background-color: ${colors.transparent};
  border: none;
  &:hover {
    background-color: ${colors.errorLight};
  }
`;

export const DeleteMenuSelector = ({ onClick, menuId }: Props) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const navigate = useNavigate();
  const { mutateAsync: deleteMenu, isLoading } = useDeleteMenu();

  const handleDeleteMenu = async () => {
    try {
      await deleteMenu({ id: menuId, organizationKeyName });
      setModalOpen(false);
      navigate(routes.menuList(organizationKeyName));
      toastr.success("Success", "Menu successfully deleted");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  return (
    <>
      <button
        css={deleteButtonStyle}
        onClick={async () => {
          if (onClick) {
            onClick();
          }
          setModalOpen(true);
        }}
      >
        <div css={menuIconStyle}>
          <FontAwesomeIcon icon={solid("trash-can")} color={colors.errorDark} />
        </div>
        <StyledText color={colors.errorDark} className="bodyTextSemibold mb-0">
          Delete menu
        </StyledText>
      </button>
      <ConfirmActionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Are you sure?"
        subtitle="Deleting this menu cannot be undone"
        confirmButton={
          <DeleteButton onClick={handleDeleteMenu} type="button">
            {isLoading ? (
              <FontAwesomeIcon icon={solid("spinner")} pulse spin />
            ) : (
              "Delete"
            )}
          </DeleteButton>
        }
      />
    </>
  );
};
